// Fund Tracker on the home page, as a product rather than a link.
//
// The question it answers is the one people actually ask: "if I had put this much in back then,
// what would I have now?" Answered from the NAV the fund really printed each month, never from an
// assumed rate, and it shows the drop along the way as plainly as the gain. A brochure's 12% is a
// wish; this is the arithmetic on what happened.
import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from '@/lib/siteLink';
import { ArrowRight, TrendingUp } from 'lucide-react';
import FundPicker from '@/components/FundPicker';
import DataDisclaimer from '@/components/DataDisclaimer';

const API = process.env.REACT_APP_BACKEND_URL;

const AMOUNTS = [50000, 100000, 500000];
const SIP_AMOUNTS = [5000, 10000, 25000];
const YEARS = [1, 3, 5];

const inr = (n) =>
  typeof n === 'number'
    ? `₹${Math.round(n).toLocaleString('en-IN')}`
    : '–';

const monthLabel = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(mo) - 1]} ${y}`;
};

// A plain inline sparkline. No chart library for eight kilobytes of line.
function Spark({ points }) {
  const d = useMemo(() => {
    if (!points || points.length < 2) return '';
    const vals = points.map((p) => p.value);
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const span = hi - lo || 1;
    return points
      .map((p, i) => {
        const x = (i / (points.length - 1)) * 100;
        const y = 30 - ((p.value - lo) / span) * 28;
        return `${i ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(' ');
  }, [points]);
  if (!d) return null;
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-full h-12" aria-hidden="true">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export default function FundTrackerBand({ category = 'large-cap' }) {
  const [mode, setMode] = useState('lumpsum');
  const [amount, setAmount] = useState(100000);
  const [years, setYears] = useState(3);
  const [code, setCode] = useState('');
  const [codeLabel, setCodeLabel] = useState('');
  const [table, setTable] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [failed, setFailed] = useState(false);

  // The ranked table doubles as the fund picker, so both come from one call.
  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/funds/tracker?category=${category}&limit=6`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => { if (alive) {
          setTable(d);
          setCode((c) => c || d?.funds?.[0]?.code || '');
          setCodeLabel((l) => l || d?.funds?.[0]?.name || '');
        } })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [category]);

  const loadGrowth = useCallback(() => {
    if (!code) return;
    const q = `code=${code}&amount=${amount}&years=${years}&mode=${mode}`;
    fetch(`${API}/api/funds/growth?${q}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(setGrowth)
      .catch(() => setGrowth(null));
  }, [code, amount, years, mode]);
  useEffect(loadGrowth, [loadGrowth]);

  const presets = mode === 'sip' ? SIP_AMOUNTS : AMOUNTS;

  // Switching mode should not leave a ₹5,00,000 monthly SIP on screen.
  const switchMode = (m) => {
    setMode(m);
    setAmount(m === 'sip' ? 10000 : 100000);
  };

  if (failed || !table?.ready || !table?.funds?.length) return null;

  const up = growth && growth.gain >= 0;

  return (
    <section className="bg-gradient-to-b from-emerald-50/60 to-white border-y border-emerald-100 py-14"
             data-testid="fund-tracker-band">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 gap-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-emerald-700">Fund Tracker</p>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">
              If you had invested, what would you have now?
            </h2>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">
              Worked out from the NAV each fund actually printed, month by month. Not a projection,
              not an assumed 12%.
            </p>
          </div>
          <Link to="/finance/funds"
                className="text-sm text-emerald-700 hover:text-emerald-800 font-medium flex items-center gap-1 shrink-0">
            All funds <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid lg:grid-cols-5 gap-4">
          {/* the controls */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
              {[['lumpsum', 'One-time'], ['sip', 'Every month']].map(([m, label]) => (
                <button key={m} onClick={() => switchMode(m)}
                  className={`flex-1 h-9 rounded-md text-sm font-medium transition-colors ${
                    mode === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                  {label}
                </button>
              ))}
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {mode === 'sip' ? 'Amount each month' : 'Amount invested'}
              </label>
              <div className="mt-2 flex flex-wrap gap-2">
                {presets.map((a) => (
                  <button key={a} onClick={() => setAmount(a)}
                    className={`h-9 px-3 rounded-lg text-sm font-medium border transition-colors ${
                      amount === a ? 'bg-gray-900 text-white border-gray-900'
                                   : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'}`}>
                    {inr(a)}
                  </button>
                ))}
              </div>
              <input type="number" min="500" step="500" value={amount}
                     onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))}
                     className="mt-2 w-full h-10 px-3 rounded-lg border border-gray-300 text-sm text-gray-900 bg-white
                                focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                     aria-label="Amount in rupees" />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Starting</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {YEARS.map((y) => (
                  <button key={y} onClick={() => setYears(y)}
                    className={`h-9 px-3 rounded-lg text-sm font-medium border transition-colors ${
                      years === y ? 'bg-gray-900 text-white border-gray-900'
                                  : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'}`}>
                    {y} year{y > 1 ? 's' : ''} ago
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider" htmlFor="ft-fund">Fund</label>
              <div className="mt-2">
                <FundPicker value={code} label={codeLabel}
                            onPick={(f) => { setCode(f.code); setCodeLabel(f.name); }} />
              </div>
              <p className="mt-1.5 text-xs text-gray-500">Any scheme AMFI publishes, not just the ones ranked below.</p>
            </div>
          </div>

          {/* the answer */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 p-5 flex flex-col">
            {!growth ? (
              <p className="text-sm text-gray-500 m-auto">Working it out…</p>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  {mode === 'sip' ? `${inr(growth.amount)} a month into ` : `${inr(growth.invested)} into `}
                  <span className="font-semibold text-gray-900">{growth.scheme.name}</span>
                  {growth.start_month ? `, from ${monthLabel(growth.start_month)}` : ''}
                </p>

                <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className={`text-4xl font-bold tabular-nums ${up ? 'text-emerald-700' : 'text-red-600'}`}>
                    {inr(growth.final_value)}
                  </span>
                  <span className="text-sm text-gray-600">
                    today, from {inr(growth.invested)} put in
                  </span>
                </div>

                <div className={`mt-1 text-sm font-medium ${up ? 'text-emerald-700' : 'text-red-600'}`}>
                  {up ? '+' : ''}{inr(growth.gain)} ({growth.gain_pct}%)
                  {growth.cagr_pct != null && <span className="text-gray-500 font-normal"> · {growth.cagr_pct}% a year</span>}
                </div>

                <div className={`mt-3 ${up ? 'text-emerald-600' : 'text-red-500'}`}>
                  <Spark points={growth.points} />
                </div>

                <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  {growth.worst_vs_invested_pct != null && (
                    <div>
                      <dt className="text-xs text-gray-500">Worst you were down</dt>
                      <dd className="font-semibold tabular-nums text-gray-900">
                        {growth.worst_vs_invested_pct}% on the money in by then
                      </dd>
                    </div>
                  )}
                  {growth.worst_drop_pct != null && (
                    <div>
                      <dt className="text-xs text-gray-500">Biggest fall from a high</dt>
                      <dd className="font-semibold tabular-nums text-gray-900">{growth.worst_drop_pct}%</dd>
                    </div>
                  )}
                </dl>

                {/* Never quietly answer a shorter question than the one asked. */}
                {growth.history_note && (
                  <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    {growth.history_note}
                  </p>
                )}
                <DataDisclaimer source={growth.source} disclaimer={growth.disclaimer}
                                className="mt-3" />
              </>
            )}
          </div>
        </div>

        {/* the ranking, kept close so the tool and the shortlist read as one product */}
        <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500">
                {['Scheme', `${table.tenor_label} rolling`, 'vs index'].map((h) => (
                  <th key={h} className="text-left font-semibold text-[11px] uppercase tracking-wider px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.funds.map((f) => (
                <tr key={f.code}
                    onClick={() => { setCode(f.code); setCodeLabel(f.name); }}
                    className={`border-t border-gray-100 cursor-pointer hover:bg-emerald-50/50 ${
                      f.code === code ? 'bg-emerald-50' : ''}`}>
                  <td className="px-4 py-3">
                    <span className="font-medium text-gray-900">{f.name}</span>
                    <span className="block text-xs text-gray-500">{f.amc}</span>
                  </td>
                  <td className="px-4 py-3 font-mono tabular-nums whitespace-nowrap">
                    {typeof f.rolling_median === 'number' ? `${f.rolling_median.toFixed(1)}%` : '–'}
                  </td>
                  <td className={`px-4 py-3 font-mono tabular-nums whitespace-nowrap ${
                    f.vs_index_pts > 0 ? 'text-emerald-700' : f.vs_index_pts < 0 ? 'text-amber-700' : ''}`}>
                    {typeof f.vs_index_pts === 'number'
                      ? `${f.vs_index_pts > 0 ? '+' : ''}${f.vs_index_pts.toFixed(2)}`
                      : '–'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-600 flex items-start gap-2">
            <TrendingUp className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Against the {table.benchmark}.{table.covers ? ` Windows cover ${table.covers}.` : ''}
              {table.period_note ? ' These windows sit inside one stretch of market, so read the order as a starting point, not a track record.' : ''}
            </span>
          </p>
        </div>
      </div>
    </section>
  );
}
